import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import {
  GetCustomerByMachineQueryVariables,
  GetCustomerQueryVariables,
  GetCustomersQueryVariables,
  GetMachinesQueryVariables,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State } from "./createJob.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncQueries = {
  getCreateJobOptions: createAppAsyncThunk(
    "createJob/getCreateJobOptions",
    async (_, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getCreateJobOptions);
      if (errors) return rejectWithValue(errors);
      if (!data?.createJobOptions) return rejectWithValue("something went wrong");
      const { createJobOptions } = data;
      const { symptoms } = createJobOptions || {};
      if (symptoms) return { symptoms };
      else return rejectWithValue("Something went wrong");
    }
  ),
  getCustomer: createAppAsyncThunk(
    "createJob/getCustomer",
    async (variables: GetCustomerQueryVariables, { rejectWithValue, getState, extra: { sdk } }) => {
      const { createJob } = getState();
      let existingCustomer = createJob.customers.find((c) => c.name === variables.nameFilter);
      if (existingCustomer) return existingCustomer;
      const { data, errors } = await graphqlRequest(sdk.getCustomer, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.customer) return rejectWithValue("something went wrong");
      return data.customer;
    }
  ),
  getCustomerByMachine: createAppAsyncThunk(
    "createJob/getCustomerByMachine",
    async (
      variables: GetCustomerByMachineQueryVariables,
      { rejectWithValue, getState, extra: { sdk } }
    ) => {
      const { data, errors } = await graphqlRequest(sdk.getCustomerByMachine, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.customerByMachine) return rejectWithValue("something went wrong");
      return data.customerByMachine;
    }
  ),
  getCustomers: createAppAsyncThunk(
    "createJob/getCustomers",
    async (variables: GetCustomersQueryVariables, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getCustomers, { variables });
      if (errors) return rejectWithValue(errors);
      if (!data?.customers) return rejectWithValue("something went wrong");
      return data.customers;
    }
  ),
  getMachines: createAppAsyncThunk(
    "createJob/getMachines",
    async (variables: GetMachinesQueryVariables, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getMachines, { variables });
      if (errors) return rejectWithValue(errors);
      if (!data?.machines) return rejectWithValue("something went wrong");
      return data.machines;
    }
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getCreateJobOptions.pending, (state) => {
    state.initLoading = true;
    return state;
  });
  builder.addCase(asyncQueries.getCreateJobOptions.rejected, (state) => {
    state.initLoading = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getCreateJobOptions.fulfilled,
    (state, { payload: { symptoms } }) => {
      state.initLoading = false;
      state.symptoms = [...symptoms];

      return state;
    }
  );
  builder.addCase(asyncQueries.getCustomer.pending, (state) => {
    state.customersLoading = true;
  });
  builder.addCase(asyncQueries.getCustomer.rejected, (state) => {
    state.customersLoading = false;
  });
  builder.addCase(asyncQueries.getCustomer.fulfilled, (state, { payload: customer }) => {
    state.customersLoading = false;
    const existingCustomer = state.customers.find((c) => c.id === customer.id);
    if (!existingCustomer) state.customers = [...state.customers, customer];
    return state;
  });
  builder.addCase(asyncQueries.getCustomerByMachine.pending, (state) => {
    state.customersLoading = true;
  });
  builder.addCase(asyncQueries.getCustomerByMachine.rejected, (state) => {
    state.customersLoading = false;
  });
  builder.addCase(asyncQueries.getCustomerByMachine.fulfilled, (state, { payload: customer }) => {
    state.customersLoading = false;
    const existingCustomer = state.customers.find((c) => c.id === customer.id);
    if (!existingCustomer) state.customers = [...state.customers, customer];
    return state;
  });
  builder.addCase(asyncQueries.getCustomers.pending, (state) => {
    state.customersLoading = true;
  });
  builder.addCase(asyncQueries.getCustomers.rejected, (state) => {
    state.customersLoading = false;
  });
  builder.addCase(asyncQueries.getCustomers.fulfilled, (state, { payload: customers }) => {
    state.customersLoading = false;
    state.customers = [...customers];
    return state;
  });
  builder.addCase(asyncQueries.getMachines.pending, (state) => {
    state.machinesLoading = true;
  });
  builder.addCase(asyncQueries.getMachines.rejected, (state, { payload: errors }) => {
    state.machinesLoading = false;
  });
  builder.addCase(asyncQueries.getMachines.fulfilled, (state, { payload: machines }) => {
    state.machinesLoading = false;
    state.machines = [...machines];
    return state;
  });
};
